import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Config } from 'src/app/core/shared/config/config';
import { AuthStateService } from './auth-state-service';
import { TokenService } from '../services/tokenservice/token.service';
import { LogoutService } from './logout.service';
import { CodeChallengeService } from '../services/code-challenge/code-challenge.service';

@Injectable({
  providedIn: 'root',
})
export class AuthPingService {
  private config: Config;

  private isRefreshScheduled: boolean = false;
  private logoutTimeout: any;
  private notificationTimeout: any;

  private notificationSubject = new BehaviorSubject<{ show: boolean; countdown: number }>({
    show: false,
    countdown: 0,
  });

  public notification$ = this.notificationSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authStateService: AuthStateService,
    private tokenService: TokenService,
    private logoutService: LogoutService,
    private codeChallengeService: CodeChallengeService
  ) {
    this.config = new Config();
    if (this.authStateService.getAuthMethod() === 'ping' && this.tokenService.hasValidToken()) {
      this.restoreTokenRefresh();
    } else {
      this.tokenService.clearTokens();
    }
  }

  obtainToken(code: string): Observable<any> {
    let codeVerifier: string | null = null;

    try {
      codeVerifier = localStorage.getItem(this.codeChallengeService.getCodeVerifierKey());
    } catch (err) {
      console.error('[authservice] erro ao acessar localStorage:', err);
    }

    if (!codeVerifier) {
      console.error('[authservice] code_verifier não encontrado no localStorage. Logout será executado.');
      this.logoutService.logout('session');
      return throwError(() => new Error('Code verifier not found'));
    }

    const body = new URLSearchParams();
    body.set('grant_type', this.config.grant_type);
    body.set('client_id', this.config.client_id);
    body.set('code', code);
    body.set('redirect_uri', this.config.redirect_uri);
    body.set('code_verifier', codeVerifier);

    return this.http.post<any>(`${this.config.url_base}/as/token.oauth2`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).pipe(
      tap((response) => {
        this.tokenService.setToken(response.access_token);
        this.tokenService.setRefreshToken(response.refresh_token);

        this.scheduleTokenRefresh(response.expires_in);
        this.scheduleLogout(response.expires_in);
      }),
      catchError((error) => {
        console.error('[authservice] Erro ao obter o token:', error);
        return throwError(() => error);
      })
    );
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.tokenService.getRefreshToken();

    if (!refreshToken) {
      console.error('[authservice] nenhum refresh_token encontrado. Logout forçado.');
      this.logoutService.logout('session');
      return throwError(() => new Error('No refresh token available'));
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken);

    return this.http.post<any>(`${this.config.url_base}/as/token.oauth2`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).pipe(
      tap((response) => {
        this.tokenService.setToken(response.access_token);
        this.tokenService.setRefreshToken(response.refresh_token);
        this.scheduleLogout(response.expires_in);
      }),
      catchError((error) => {
        console.error('[authservice] Erro ao renovar o token:', error);
        this.tokenService.clearTokens();
        return throwError(() => error);
      })
    );
  }

  private scheduleTokenRefresh(expires_in: number): void {
    if (this.isRefreshScheduled) {
      return;
    }

    this.isRefreshScheduled = true;
    const refreshTime = Math.max((expires_in - 60), 10) * 1000;

    setTimeout(() => {
      this.refreshToken().subscribe({
        next: () => {
          this.isRefreshScheduled = false;
        },
        error: (err) => {
          console.error('[authservice] Erro ao renovar o token:', err);
        }
      });
    }, refreshTime);
  }

  private scheduleLogout(expires_in: number): void {
    if (expires_in <= 20) {
      console.warn('[authservice] tempo de expiração muito curto. Logout imediato evitado.');
      return;
    }

    if (this.logoutTimeout) clearTimeout(this.logoutTimeout);
    if (this.notificationTimeout) clearTimeout(this.notificationTimeout);

    const logoutTime = (expires_in - 20) * 1000;

    this.logoutTimeout = setTimeout(() => {
      this.logoutService.logout('session');
    }, logoutTime);

    this.notificationTimeout = setTimeout(() => {
      this.notificationSubject.next({ show: true, countdown: 5 });
      this.startCountdown();
    }, logoutTime - 5000);
  }

  private startCountdown(): void {
    let countdown = 5;
    this.notificationSubject.next({ show: true, countdown });

    const interval = setInterval(() => {
      countdown--;
      this.notificationSubject.next({ show: true, countdown });
      if (countdown <= 0) {
        clearInterval(interval);
        window.location.reload();
      }
    }, 1000);
  }

  private restoreTokenRefresh(): void {
    const token = this.tokenService.getToken();
    if (!token) return;

    const decodedToken = this.tokenService.decodeTheToken(token);
    if (!decodedToken?.exp) return;

    const expiresIn = decodedToken.exp - Math.floor(Date.now() / 1000);

    if (expiresIn > 0) {
      this.scheduleTokenRefresh(expiresIn);
      this.scheduleLogout(expiresIn);
    } else {
      console.warn('[authservice] Token expirado no restore. tentando renovar com o refresh_toke.');

      this.refreshToken().subscribe({
        next: () => {
        },
        error: () => {
          this.logoutService.logout('session');
        }
      });
    }
  }

  startPingLogin(): void {
    this.authStateService.setAuthMethod('ping');

    const codeVerifier = this.codeChallengeService.generateCodeVerifier();
    this.codeChallengeService.storeCodeVerifier(codeVerifier);

    this.codeChallengeService.generateCodeChallenge(codeVerifier).then((codeChallenge) => {
      const authUrl = `${this.config.url_base}/as/authorization.oauth2?client_id=${this.config.client_id}&response_type=${this.config.response_type}&redirect_uri=${this.config.redirect_uri}&vnd_pi_application_name=${this.config.policy}&scope=openid&code_challenge=${codeChallenge}&code_challenge_method=S256`;
      window.location.href = authUrl;
    });
  }
}
